import React, { useEffect, useState } from 'react';
import { Table, Button, Select, message, Space, Card, Typography, Modal, Form, Input, InputNumber } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getInfraServers, addInfraServer, updateInfraServer, syncVncBasePorts, deleteInfraServer } from '../services/api';
import { useSidebar } from '../contexts/SidebarContext';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from '../components/shared/PageHeader';

interface InfraServerRow {
  id: number;
  hostName: string;
  port: number;
  serverInfo: string;
  baseVncPort: number | null;
  capabilities?: string | null;
  k3sPoolLabel?: string | null;
  site?: string | null;
  remarks?: string;
}

const DEFAULT_BASE = '__default__';
const BASE_OPTIONS = [
  { value: DEFAULT_BASE, label: '(default 5900)' },
  { value: 5900, label: '5900' },
  { value: 7000, label: '7000' },
];

const ServerInfoPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const [servers, setServers] = useState<InfraServerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dirty, setDirty] = useState<Record<number, number | null>>({});
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm] = Form.useForm();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<InfraServerRow | null>(null);
  const [editing, setEditing] = useState(false);
  const [editForm] = Form.useForm();

  const loadServers = async () => {
    setLoading(true);
    try {
      const data = await getInfraServers();
      setServers(Array.isArray(data) ? data : []);
      setDirty({});
    } catch (e) {
      message.error('Failed to load servers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  const handleBaseChange = (serverId: number, value: number | null) => {
    setDirty((prev) => ({ ...prev, [serverId]: value }));
  };

  const handleSave = async (record: InfraServerRow) => {
    const newBase = dirty[record.id];
    if (newBase === undefined) return;
    try {
      await updateInfraServer(record.id, { baseVncPort: newBase });
      message.success(`Saved base VNC port for ${record.serverInfo}`);
      setDirty((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      setServers((prev) =>
        prev.map((s) => (s.id === record.id ? { ...s, baseVncPort: newBase } : s))
      );
    } catch (e) {
      message.error('Failed to save');
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await syncVncBasePorts();
      if (res?.success) {
        message.success(res.message || 'Sync completed');
        await loadServers();
      } else {
        message.error(res?.message || 'Sync failed');
      }
    } catch (e) {
      message.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleAddServer = async () => {
    try {
      const values = await addForm.validateFields();
      setAdding(true);
      await addInfraServer({
        hostName: values.hostName.trim(),
        port: values.port,
        remarks: values.remarks?.trim() || undefined,
        capabilities: values.capabilities?.trim() || undefined,
        k3sPoolLabel: values.k3sPoolLabel?.trim() || undefined,
        site: values.site?.trim() || undefined,
      });
      message.success(`Added server ${values.hostName}:${values.port}`);
      setAddModalOpen(false);
      addForm.resetFields();
      await loadServers();
    } catch (e: any) {
      if (e?.errorFields) return; // form validation error, already shown inline
      message.error(e?.response?.data?.message || 'Failed to add server');
    } finally {
      setAdding(false);
    }
  };

  const handleEditServer = async () => {
    if (!editingRecord) return;
    try {
      const values = await editForm.validateFields();
      setEditing(true);
      await updateInfraServer(editingRecord.id, {
        hostName: values.hostName.trim(),
        port: values.port,
        remarks: values.remarks?.trim() || undefined,
        capabilities: values.capabilities?.trim() || null,
        k3sPoolLabel: values.k3sPoolLabel?.trim() || null,
        site: values.site?.trim() || null,
      });
      message.success(`Updated server ${values.hostName}:${values.port}`);
      setEditModalOpen(false);
      setEditingRecord(null);
      editForm.resetFields();
      await loadServers();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Failed to update server');
    } finally {
      setEditing(false);
    }
  };

  // Port fields are pre-filled (Edit) or start empty (Add); either way, a click-to-focus
  // followed by typing must REPLACE the existing digits, not append to them - appending
  // (e.g. existing "23454" + typed "30000" digit-by-digit) can transiently or finally
  // exceed the 65535 max and gets silently clamped. Select-all on both focus (keyboard
  // tab-in) and click (mouse-in, which otherwise overrides focus-time selection with its
  // own caret placement) fixes this - both handlers run synchronously within the same
  // event dispatch, so there's no race with the next keystroke (unlike a deferred
  // setTimeout-based select, which can lose a character if typing starts before it fires).
  const selectPortText = (e: React.SyntheticEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  // InputNumber's own min/max props silently clamp out-of-range values to the
  // boundary on blur (e.g. typing 456741 became 65535 with no feedback) -
  // instead, the fields below drop min/max and rely on this validator so an
  // out-of-range port is rejected with a visible error, not silently rewritten.
  const validatePortRange = (_: unknown, value: number | null | undefined) => {
    if (value === null || value === undefined) return Promise.resolve();
    if (value < 1 || value > 65535) {
      return Promise.reject(new Error('Port must be between 1 and 65535'));
    }
    return Promise.resolve();
  };

  const openEditModal = (record: InfraServerRow) => {
    setEditingRecord(record);
    editForm.setFieldsValue({
      hostName: record.hostName,
      port: record.port,
      remarks: record.remarks,
      capabilities: record.capabilities ?? undefined,
      k3sPoolLabel: record.k3sPoolLabel ?? undefined,
      site: record.site ?? undefined,
    });
    setEditModalOpen(true);
  };

  const handleDelete = (record: InfraServerRow) => {
    Modal.confirm({
      title: 'Delete server record?',
      content: (
        <span>
          Remove <strong>{record.serverInfo}</strong> from Server Info? Infra user accounts that reference this server are unchanged; Restart VNC will use default base port (5900) for them. This action is audited.
        </span>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteInfraServer(record.id);
          message.success('Server record deleted');
          await loadServers();
        } catch (e) {
          message.error('Failed to delete server');
        }
      },
    });
  };

  const columns = [
    { title: 'Host', dataIndex: 'hostName', key: 'hostName', width: 200 },
    { title: 'Port', dataIndex: 'port', key: 'port', width: 80 },
    { title: 'Server Info', dataIndex: 'serverInfo', key: 'serverInfo' },
    {
      title: 'Base VNC Port',
      key: 'baseVncPort',
      width: 200,
      render: (_: unknown, record: InfraServerRow) => {
        const raw = dirty[record.id] !== undefined ? dirty[record.id] : record.baseVncPort;
        const value = raw === null || raw === undefined ? DEFAULT_BASE : raw;
        const isDirty = dirty[record.id] !== undefined;
        return (
          <Space>
            <Select
              value={value}
              onChange={(v) => handleBaseChange(record.id, v === DEFAULT_BASE ? null : (v as number))}
              options={BASE_OPTIONS}
              style={{ width: 140 }}
              placeholder="Default 5900"
            />
            {isDirty && (
              <Button type="primary" size="small" onClick={() => handleSave(record)}>
                Save
              </Button>
            )}
          </Space>
        );
      },
    },
    { title: 'Capabilities', dataIndex: 'capabilities', key: 'capabilities', width: 140, ellipsis: true },
    { title: 'k3s Pool', dataIndex: 'k3sPoolLabel', key: 'k3sPoolLabel', width: 120, ellipsis: true },
    { title: 'Site', dataIndex: 'site', key: 'site', width: 100, ellipsis: true },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_: unknown, record: InfraServerRow) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            Edit
          </Button>
          <Button type="link" danger size="small" onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="Server Info"
            purpose="Base VNC port used to compute the display number for Restart VNC. Changes are audited."
            actions={
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
                  Add Server
                </Button>
                <Button type="default" loading={syncing} onClick={handleSyncAll}>
                  Sync VNC bases from servers
                </Button>
              </Space>
            }
          />
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <Typography.Title level={4} className="!mb-0">
                Server Info (Infra Servers)
              </Typography.Title>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
                  Add Server
                </Button>
                <Button type="default" loading={syncing} onClick={handleSyncAll}>
                  Sync VNC bases from servers
                </Button>
              </Space>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Base VNC port (5900 or 7000) is used to compute display number for Restart VNC. Default is 5900. Changes are audited. You can delete server records that no longer exist; infra user rows that reference that server are unchanged and Restart VNC will use default base (5900) for them.
            </p>
          </>
        )}
        {useNew && (
          <p className="text-xs text-gray-400 mb-4 -mt-2">
            Default base is 5900. You can delete server records that no longer exist; infra user rows that reference that server are unchanged and Restart VNC will use default base (5900) for them.
          </p>
        )}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={servers}
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>
      <Modal
        title="Add Server"
        open={addModalOpen}
        onOk={handleAddServer}
        confirmLoading={adding}
        onCancel={() => {
          setAddModalOpen(false);
          addForm.resetFields();
        }}
        okText="Add"
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="hostName"
            label="Host Name"
            rules={[{ required: true, message: 'Host name is required' }]}
          >
            <Input placeholder="e.g. prac4v.trg.vlsiexpert.in" />
          </Form.Item>
          <Form.Item
            name="port"
            label="Port"
            rules={[
              { required: true, message: 'Port is required' },
              { validator: validatePortRange },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g. 23454"
              controls={false}
              onFocus={selectPortText}
              onClick={selectPortText}
            />
          </Form.Item>
          <Form.Item name="capabilities" label="Capabilities">
            <Input placeholder="e.g. legacy-vnc,k3s" />
          </Form.Item>
          <Form.Item name="k3sPoolLabel" label="k3s Pool Label">
            <Input placeholder="Optional k3s node pool label" />
          </Form.Item>
          <Form.Item name="site" label="Site">
            <Input placeholder="e.g. trg, hyd" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} placeholder="Optional remarks" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Edit Server"
        open={editModalOpen}
        onOk={handleEditServer}
        confirmLoading={editing}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingRecord(null);
          editForm.resetFields();
        }}
        okText="Save"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="hostName"
            label="Host Name"
            rules={[{ required: true, message: 'Host name is required' }]}
          >
            <Input placeholder="e.g. prac4v.trg.vlsiexpert.in" />
          </Form.Item>
          <Form.Item
            name="port"
            label="Port"
            rules={[
              { required: true, message: 'Port is required' },
              { validator: validatePortRange },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g. 23454"
              controls={false}
              onFocus={selectPortText}
              onClick={selectPortText}
            />
          </Form.Item>
          <Form.Item name="capabilities" label="Capabilities">
            <Input placeholder="e.g. legacy-vnc,k3s" />
          </Form.Item>
          <Form.Item name="k3sPoolLabel" label="k3s Pool Label">
            <Input placeholder="Optional k3s node pool label" />
          </Form.Item>
          <Form.Item name="site" label="Site">
            <Input placeholder="e.g. trg, hyd" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} placeholder="Optional remarks" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServerInfoPage;
